import { useContext, useEffect, useState } from "react";

//PACKAGES
import { useNavigate } from "react-router-dom";
import moment from "moment";

//CONSTANTS
import { STRING, STRINGS } from "../../constant";

//COMPONENTS
import { Header, SpeedoMeter, LAView, LAText, Chart } from "../../components";
import { AuthContext } from "../../context/AuthProvider";
import { ThemeContext } from "../../context/ThemeProvider";
import { useAnalytics } from "../../context/AnalyticsProvider";
import CommonHeader from "../../components/common/CommonHeader";
import { faGauge, faChartLine, faBook, faClock, faClipboardCheck } from "@fortawesome/free-solid-svg-icons";
import StatsGrid from "../../components/common/StatsGrid";
import ProgressHistory from "./ProgressHistory";

let _dashboardTracked = false;

function Dashboard() {
  const navigate = useNavigate();
  const { profile } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const { track } = useAnalytics();
  const [score, setScore] = useState(null);

  useEffect(() => {
    setScore(profile?.readiness_scores);
  }, [profile?.readiness_scores]);

  // Track dashboard view (once per page load)
  useEffect(() => {
    if (_dashboardTracked) return;
    _dashboardTracked = true;
    track('dashboard_viewed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scores = profile?.score_progress ?? {};
  let keys = Object.keys(scores);

  const getProgressBarColor = (readinessScore) => {
    if (readinessScore < 25) return "bg-red-500";
    if (readinessScore >= 25 && readinessScore <= 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  function getChartData() {
    keys.sort(
      (a, b) =>
        moment(a, "YYYY-MM-DD").toDate().getTime() -
        moment(b, "YYYY-MM-DD").toDate().getTime()
    );

    if (keys.length > 6) {
      keys = keys.slice(keys.length - 6, keys.length);
    }

    let labels = keys.length < 6 ? [""] : [];
    let data = keys.length < 6 ? [0] : [];
    for (var index in keys) {
      if (index <= 5) {
        const key = keys[index];
        labels.push(moment(key, "YYYY-MM-DD").format("DD MMM"));

        const score = scores[key];
        if (isNaN(score) || !isFinite(score)) {
          if (data.length > 0) {
            data.push(data[data.length - 1]);
          } else {
            data.push(0);
          }
        } else {
          data.push(parseInt(score));
        }
      }
    }

    const chartData = {
      labels: labels,
      datasets: [
        {
          data: data,
          borderColor: theme?.THEME_DARK,
          backgroundColor: theme?.THEME_DARK,
        },
        {
          data: [0],
          withDots: false,
        },
        {
          data: [100],
          withDots: false,
        },
      ],
    };

    return chartData;
  }

  const getPerformanceMetrics = () => {
    const totalQuestions = profile?.readiness_scores?.questions_answered || 0;
    const correctQuestions = profile?.readiness_scores?.questions_correct || 0;
    const incorrectQuestions = profile?.readiness_scores?.questions_incorrect || 0;
    const totalAvailable = profile?.readiness_scores?.total_questions || 2228;
    const lastTest = profile?.practice_test_history?.[profile?.practice_test_history?.length - 1];
    
    let metrics = {
      accuracy: totalQuestions > 0 ? Math.round((correctQuestions / totalQuestions) * 100) : 0,
      questionsAnswered: totalQuestions,
      totalQuestions: totalAvailable,
      avgTimePerQuestion: lastTest ? Math.round(lastTest.time_taken / lastTest.total) : 0,
      testsCompleted: profile?.practice_test_history?.length || 0,
      lastTestDate: lastTest?.date || null,
      correctAnswers: correctQuestions,
      incorrectAnswers: incorrectQuestions
    };
    const stats = [
      {
        icon: faChartLine,
        iconType: 'fontawesome',
        stat: `${metrics.accuracy}%`,
        label: 'Overall Accuracy',
        subtitle: `${metrics.correctAnswers} correct / ${metrics.questionsAnswered} total`,
        color: metrics.accuracy >= 60 ? theme?.primary?.green?.[500] : 
               metrics.accuracy >= 40 ? theme?.primary?.yellow?.[500] : 
               theme?.primary?.red?.[500],
      },
      {
        icon: faBook,
        iconType: 'fontawesome',
        stat: metrics.questionsAnswered,
        label: 'Questions Answered',
        subtitle: `${((metrics.questionsAnswered / metrics.totalQuestions) * 100).toFixed(1)}% of ${metrics.totalQuestions} total`,
        color: theme?.primary?.blue?.[500],
      },
      {
        icon: faClock,
        iconType: 'fontawesome',
        stat: metrics.avgTimePerQuestion ? `${metrics.avgTimePerQuestion}s` : 'N/A',
        label: 'Avg. Time/Question',
        subtitle: metrics.avgTimePerQuestion ? 'Good pace!' : 'No test data',
        color: metrics.avgTimePerQuestion && metrics.avgTimePerQuestion < 90 ? 
               theme?.primary?.green?.[500] : theme?.primary?.yellow?.[500],
      },
      {
        icon: faClipboardCheck,
        iconType: 'fontawesome',
        stat: metrics.testsCompleted,
        label: 'Tests Completed',
        subtitle: metrics.lastTestDate ? `Last: ${metrics.lastTestDate}` : 'No tests yet',
        color: theme?.primary?.orange?.[500],
      },
    ];
    return stats;
  };

  const renderMilestoneCard = () => {
    if (!score || score.readiness_score === undefined) return null;

    const readinessScore = score.readiness_score;
    const isExamReady = readinessScore >= 80;
    const isIntermediate = readinessScore >= 50;
    
    const getMilestoneData = () => {
      if (isExamReady) {
        return {
          title: '🎉 Exam Ready!',
          target: 100,
          color: theme?.primary?.green,
          message: readinessScore >= 100 ? 'Milestone achieved! 🎉' : `Just ${(100 - readinessScore).toFixed(1)}% away from 100%!`
        };
      } else if (isIntermediate) {
        return {
          title: '🏆 Next Milestone',
          target: 80,
          color: theme?.primary?.blue,
          message: `Just ${(80 - readinessScore).toFixed(1)}% away!`
        };
      } else {
        return {
          title: '🏆 Next Milestone',
          target: 50,
          color: theme?.primary?.orange,
          message: `Just ${(50 - readinessScore).toFixed(1)}% away!`
        };
      }
    };

    const milestoneData = getMilestoneData();
    const progressPercentage = isExamReady 
      ? ((readinessScore - 80) / 20 * 100).toFixed(1)
      : isIntermediate 
        ? ((readinessScore - 50) / 30 * 100).toFixed(1)
        : ((readinessScore) / 50 * 100).toFixed(1);

    return (
      <div
        style={{
          background: `linear-gradient(135deg, ${milestoneData.color?.[100] + '33'} 0%, ${milestoneData.color?.[500] + '22'} 100%)`,
          borderRadius: '0.75rem',
          padding: '1rem',
          border: `1px solid ${milestoneData.color?.[500] + '40'}`,
          marginTop: 'auto',
          marginBottom: '1rem',
          maxWidth: '500px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5rem'
        }}>
          <div style={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: theme?.text?.primary
          }}>
            {milestoneData.title}
          </div>
          <div style={{
            fontSize: '1.125rem',
            fontWeight: 700,
            color: milestoneData.color?.[500]
          }}>
            {milestoneData.target}%
          </div>
        </div>
        <div style={{
          fontSize: '0.75rem',
          color: theme?.text?.secondary,
          marginBottom: '0.75rem'
        }}>
          {milestoneData.message}
        </div>
        <div style={{
          width: '100%',
          height: '0.5rem',
          background: theme?.borders?.primary,
          borderRadius: '0.25rem',
          overflow: 'hidden'
        }}>
          <div
            style={{
              height: '100%',
              background: `linear-gradient(90deg, ${milestoneData.color?.[500]}, ${milestoneData.color?.[400]})`,
              borderRadius: '0.25rem',
              width: `${progressPercentage}%`,
              transition: 'width 1s ease'
            }}
          />
        </div>
      </div>
    );
  };

  const renderDomainReadinessCards = () => {
    if (!score?.topic_progress || score.topic_progress.length === 0) {
      return (
        <div style={{
          background: theme?.background?.card,
          borderRadius: '1.25rem',
          padding: '2rem',
          border: `1px solid ${theme?.borders?.primary}`,
          boxShadow: theme?.shadows?.base,
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '1rem',
            color: theme?.text?.secondary,
            marginBottom: '0.5rem'
          }}>
            📊
          </div>
          <div style={{
            fontSize: '0.875rem',
            color: theme?.text?.secondary
          }}>
            {STRINGS.domain_readiness_score_empty}
          </div>
        </div>
      );
    }

    return (
      <div style={{
        background: theme?.background?.card,
        borderRadius: '1.25rem',
        padding: '2rem',
        border: `1px solid ${theme?.borders?.primary}`,
        boxShadow: theme?.shadows?.base
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            color: theme?.text?.primary,
            marginBottom: '0.5rem'
          }}>
            {STRINGS.domain_readiness_score}
          </h2>
          <p style={{
            fontSize: '0.875rem',
            color: theme?.text?.secondary
          }}>
            Track your progress across different exam domains
          </p>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem'
        }}>
          {score.topic_progress.map((domain, index) => {
            const readinessScore = Math.min(parseFloat(domain?.readiness_score) || 0, 100);
            const displayScore = Math.round(readinessScore);
            
            const getDomainStatus = (score) => {
              if (score >= 80) return { status: 'Excellent', emoji: '🎯', color: theme?.primary?.green };
              if (score >= 60) return { status: 'Good', emoji: '📈', color: theme?.primary?.blue };
              if (score >= 40) return { status: 'Fair', emoji: '⚠️', color: theme?.primary?.yellow };
              return { status: 'Needs Work', emoji: '📚', color: theme?.primary?.red };
            };

            const domainStatus = getDomainStatus(displayScore);

            return (
              <div
                key={index}
                style={{
                  background: `linear-gradient(135deg, ${domainStatus.color?.[50] + '40'} 0%, ${domainStatus.color?.[100] + '20'} 100%)`,
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  border: `1px solid ${domainStatus.color?.[200] + '60'}`,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = theme?.shadows?.lg;
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '1rem'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: theme?.text?.primary,
                      marginBottom: '0.25rem',
                      lineHeight: '1.4'
                    }}>
                      {domain?.domain_name}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: theme?.text?.secondary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <span>{domainStatus.emoji}</span>
                      <span>{domainStatus.status}</span>
                    </div>
                  </div>
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: domainStatus.color?.[600],
                    textAlign: 'right'
                  }}>
                    {displayScore}%
                  </div>
                </div>

                <div style={{
                  width: '100%',
                  height: '0.5rem',
                  background: theme?.borders?.secondary || '#e5e7eb',
                  borderRadius: '0.25rem',
                  overflow: 'hidden',
                  marginBottom: '0.75rem'
                }}>
                  <div
                    style={{
                      height: '100%',
                      background: `linear-gradient(90deg, ${domainStatus.color?.[500]}, ${domainStatus.color?.[400]})`,
                      borderRadius: '0.25rem',
                      width: `${readinessScore}%`,
                      transition: 'width 1s ease'
                    }}
                  />
                </div>

                
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const chartData = getChartData();
  const performanceMetrics = getPerformanceMetrics();
  return (
    <div>
      <CommonHeader
          title="Dashboard"
          subtitle="Track your progress and optimize your learning journey"
          quote="Progress measured is progress achieved. Insights gained guide success."
          onBack={() => navigate("/home")}
          theme={theme}
          showBackButton={true}
        />
        
        {/* Performance Stats */}
        <StatsGrid
          stats={performanceMetrics}
          theme={theme}
        />
        

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
        <div className="flex flex-col gap-6" style={{ background: theme?.background?.card, borderRadius: '1.25rem', padding: '2rem', border: `1px solid ${theme?.borders?.primary}`, boxShadow: theme?.shadows?.base }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: theme?.text?.primary, marginBottom: '0.5rem' }}>Overall Readiness Score</h2>
        <p style={{ fontSize: '0.875rem', color: theme?.text?.secondary }}>Your comprehensive exam preparation progress</p>
      </div>
          <SpeedoMeter data={score} />
        <div className="w-full justify-center items-center">
        {renderMilestoneCard()}
        </div>
        </div>
        
        <ProgressHistory 
            progressData={profile?.score_progress || {}}
            theme={theme}
          />
          </div>
                    <div className="mt-10">
            {renderDomainReadinessCards()}
          </div>
    
    
      
      </div>
  );
}

export default Dashboard;
